import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, FileText, Loader2, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import AdminLayout from '@/components/admin/AdminLayout';
import { supabase } from '@/integrations/supabase/client';
import { useAdminAuth } from '@/hooks/useAdminAuth';

interface UploadedFile { name: string; status: 'pending' | 'uploading' | 'success' | 'error'; error?: string; }

const AdminBlogBulk = () => {
  const navigate = useNavigate();
  const { user } = useAdminAuth();
  const { toast } = useToast();
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const generateSlug = (title: string) => title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  const extractTitleFromHtml = (html: string, filename: string): string => {
    const titleMatch = html.match(/<title[^>]*>([^<]+)<\/title>/i);
    if (titleMatch) return titleMatch[1].trim();
    const h1Match = html.match(/<h1[^>]*>([^<]+)<\/h1>/i);
    if (h1Match) return h1Match[1].trim();
    return filename.replace(/\.(html|htm)$/i, '').replace(/[-_]/g, ' ');
  };
  const extractBodyContent = (html: string): string => {
    const bodyMatch = html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
    return bodyMatch ? bodyMatch[1].trim() : html;
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles) return;
    const htmlFiles = Array.from(selectedFiles).filter(f => f.name.endsWith('.html') || f.name.endsWith('.htm'));
    if (htmlFiles.length === 0) { toast({ title: 'Invalid files', description: 'Please select HTML files only', variant: 'destructive' }); return; }
    setFiles(htmlFiles.map(f => ({ name: f.name, status: 'pending' })));
    processFiles(htmlFiles);
  };

  const processFiles = async (htmlFiles: File[]) => {
    setUploading(true);
    let completed = 0;
    for (let i = 0; i < htmlFiles.length; i++) {
      const file = htmlFiles[i];
      setFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'uploading' } : f));
      try {
        const content = await file.text();
        const title = extractTitleFromHtml(content, file.name);
        const bodyContent = extractBodyContent(content);
        const slug = `${generateSlug(title)}-${Date.now()}`;
        const { error } = await supabase.from('blog_posts').insert({ title, slug, content: bodyContent, status: 'draft', author_id: user?.id });
        if (error) throw error;
        setFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'success' } : f));
      } catch (error: any) {
        setFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'error', error: error.message } : f));
      }
      completed++;
      setProgress((completed / htmlFiles.length) * 100);
    }
    setUploading(false);
    toast({ title: 'Upload complete', description: 'Posts imported successfully' });
  };

  const getStatusIcon = (status: UploadedFile['status']) => {
    switch (status) {
      case 'pending': return <FileText className="h-4 w-4 text-muted-foreground" />;
      case 'uploading': return <Loader2 className="h-4 w-4 animate-spin text-primary" />;
      case 'success': return <Check className="h-4 w-4 text-green-500" />;
      case 'error': return <X className="h-4 w-4 text-destructive" />;
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-2xl">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/blog')}><ArrowLeft className="h-4 w-4" /></Button>
          <div><h1 className="text-3xl font-bold text-foreground">Bulk Upload</h1><p className="text-muted-foreground mt-1">Import multiple blog posts from HTML files</p></div>
        </div>
        <Card>
          <CardHeader><CardTitle>Upload HTML Files</CardTitle><CardDescription>Select HTML files to import as blog posts.</CardDescription></CardHeader>
          <CardContent className="space-y-6">
            <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center">
              <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">Select HTML files to import</p>
              <Input type="file" accept=".html,.htm" multiple onChange={handleFileSelect} className="hidden" id="bulk-upload" disabled={uploading} />
              <Button asChild disabled={uploading}><label htmlFor="bulk-upload" className="cursor-pointer">Select Files</label></Button>
            </div>
            {files.length > 0 && (
              <div className="space-y-4">
                {uploading && <div className="space-y-2"><div className="flex justify-between text-sm"><span>Uploading...</span><span>{Math.round(progress)}%</span></div><Progress value={progress} /></div>}
                <div className="space-y-2">
                  {files.map((file, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-3">{getStatusIcon(file.status)}<span className="text-sm">{file.name}</span></div>
                      {file.error && <span className="text-xs text-destructive">{file.error}</span>}
                    </div>
                  ))}
                </div>
                {!uploading && <Button onClick={() => navigate('/admin/blog')} className="w-full">View All Posts</Button>}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminBlogBulk;
